/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
/* eslint-disable  @typescript-eslint/no-empty-function */
import { captureException } from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { CONVERT } from 'components/admin/utils/feature-flags';
import { logMaxRetriesAttempted } from 'components/sales/utils/eventUtils';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useUser } from 'controllers/contexts/user';
import { api } from 'controllers/network/apiClient';
import { DateTime } from 'luxon';
import useAuth from 'hooks/useAuth';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WEBSOCKET_HOST } from 'utils/envVars';
import { RESPONSES_QUERY_KEYS } from '../constants';
import { getIncrementalBackoff } from '../utils/getIncrementalBackoff';

export type WebSocketMessageType =
  | 'ping'
  | 'pong'
  | 'user_joined'
  | 'user_left'
  | 'broadcast_to_channel'
  | 'brodcast_to_shop'
  | 'message_sent'
  | 'new_inbound_message'
  | 'unresolved_subscriber_chat'
  | 'resolved_subscriber_chat'
  | 'assignments_updated'
  | 'new_outbound_message'
  | 'subscriber_event_stream_updated';

type WebSocketMessageFromUser = {
  username: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
};

type WebSocketMessage = {
  type?: WebSocketMessageType;
  channel?: string;
  message?: any;
  from_user?: WebSocketMessageFromUser;
  timestamp?: string;
  subscriber_id?: number;
  data?: Record<string, any>;
};

export interface WebSocketOutgoingMessage {
  routeKey: string;
  [key: string]: any;
}

export interface UseWebSocket {
  isConnected: boolean;
  isMaxRetriesAttempted: boolean;
  messages: WebSocketMessage[];
  lastMessage: WebSocketMessage | null;
  send: (message: Record<string, any>) => void;
}

export const WebSocketContext = createContext<UseWebSocket>({
  messages: [],
  lastMessage: null,
  isConnected: false,
  isMaxRetriesAttempted: false,
  send: () => {
    throw new Error('Not implemented');
  },
});

// Recursively parse JSON strings into objects
export const deepJsonParse = (json: string): Record<string, any> => {
  return JSON.parse(json, (key, value) => {
    if (typeof value === 'string') {
      try {
        return deepJsonParse(value);
      } catch {
        return value;
      }
    }
    return value;
  });
};

const getTicket = (): Promise<{ ticket: string }> =>
  api.post('/v2/websockets/ticket');

const RETRY_REPORT_THRESHOLD = 5;
const MAX_RECONNECT_DELAY_MS = 60_000;
const WEBSOCKET_HEARTBEAT_MS = 60_000;

const getReconnectDelay = (attempt: number): number => {
  const ceiling = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** attempt);
  return ceiling / 2 + Math.random() * (ceiling / 2);
};

export const WebSocketProvider: React.FC<{ baseUrl?: string }> = ({
  children,
  baseUrl = WEBSOCKET_HOST,
}) => {
  const {
    user: { id: user_id },
  } = useUser();
  const socket = useRef<WebSocket | null>(null);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isMaxRetriesAttempted, setIsMaxRetriesAttempted] = useState(false);
  const { hasLabsFlag, hasInitialized } = usePSLabs();
  const { isHistorySession } = useAuth();
  const canConnect =
    !isHistorySession && hasInitialized && hasLabsFlag(CONVERT);
  const retryCount = useRef(1);
  const hasReportedRetryLimit = useRef(false);

  const {
    data: ticket,
    refetch: refetchTicket,
    isFetching: isFetchingTicket,
  } = useQuery({
    queryKey: [RESPONSES_QUERY_KEYS.WEBSOCKET_TICKET],
    queryFn: getTicket,
    select: (data) => data?.ticket,
    enabled: canConnect,
    refetchOnWindowFocus: !socket.current,
    retry: true,
    retryDelay: (attempt) => getReconnectDelay(attempt + 1),
  });

  const send = (message: Record<string, any>, retry = 1) => {
    if (isHistorySession) return;
    if (socket.current?.readyState === WebSocket.OPEN) {
      const timestamp = DateTime.utc().toISO();
      retryCount.current = 1;
      socket.current?.send(
        JSON.stringify({ ...message, created_at: timestamp }),
      );
      setIsMaxRetriesAttempted(false);
    } else if (retry <= 3) {
      setTimeout(() => {
        send(message, retry + 1);
      }, getIncrementalBackoff(retry));
    }
  };

  useEffect(() => {
    if (!ticket || !baseUrl || isFetchingTicket || !canConnect) {
      return;
    }

    const search = new URLSearchParams();
    search.set('ticket', ticket);
    // instantiate a new websocket connection with the ticket
    socket.current = new WebSocket(`${baseUrl}?${search.toString()}`);
    let heartbeatId: number | undefined;
    let retryTimeoutId: number | undefined;
    let cancelled = false;

    socket.current.onmessage = (message) => {
      let parsedMessage: WebSocketMessage;
      // If the message is not JSON, ignore it.
      try {
        parsedMessage = deepJsonParse(message.data);
      } catch {
        return;
      }

      // If message is a ping, respond with a pong. Otherwise add it to the list of messages.
      if (parsedMessage.type === 'ping') {
        send({ timestamp: parsedMessage.timestamp, routeKey: 'pong' });
        return;
      }

      if (parsedMessage.type === 'pong') {
        return;
      }

      setLastMessage(parsedMessage);
      setMessages((prevMessages) => [...prevMessages, parsedMessage]);
    };

    socket.current.onopen = () => {
      setIsConnected(true);
      retryCount.current = 1;
      hasReportedRetryLimit.current = false;
      setIsMaxRetriesAttempted(false);
      heartbeatId = window.setInterval(() => {
        if (socket.current?.readyState === WebSocket.OPEN) {
          socket.current.send(JSON.stringify({ routeKey: 'pong' }));
        }
      }, WEBSOCKET_HEARTBEAT_MS);
    };

    socket.current.onclose = (e) => {
      if (heartbeatId !== undefined) window.clearInterval(heartbeatId);
      if (cancelled) return;
      setIsConnected(false);
      retryTimeoutId = window.setTimeout(() => {
        if (cancelled) return;
        if (
          retryCount.current >= RETRY_REPORT_THRESHOLD &&
          !hasReportedRetryLimit.current
        ) {
          captureException(
            new Error('Websocket connection retry threshold reached'),
            {
              extra: {
                user_id,
                code: e.code,
                was_clean: e.wasClean,
                reason: e.reason,
              },
            },
          );
          logMaxRetriesAttempted(user_id);
          setIsMaxRetriesAttempted(true);
          hasReportedRetryLimit.current = true;
        }
        retryCount.current += 1;
        refetchTicket();
      }, getReconnectDelay(retryCount.current));
    };

    socket.current.onerror = () => {
      socket.current?.close();
    };

    return () => {
      cancelled = true;
      if (retryTimeoutId !== undefined) window.clearTimeout(retryTimeoutId);
      if (heartbeatId !== undefined) window.clearInterval(heartbeatId);
      socket.current?.close();
      socket.current = null;
    };
  }, [baseUrl, ticket, isFetchingTicket, canConnect]);

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        isMaxRetriesAttempted,
        messages,
        lastMessage,
        send,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

const useWebSocket = (): UseWebSocket => {
  return useContext(WebSocketContext);
};

export default useWebSocket;
